import { ChangeDetectionStrategy, Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { connectState } from '@examdojo/angular/util';
import { CategoryQuery, HydratedSyllabusTopicsTree } from '@examdojo/category/v2';
import { InfoIconComponent } from '@examdojo/core/info-icon';
import { distinctUntilDeepChanged } from '@examdojo/core/rxjs';
import { TranslocoPipe } from '@jsverse/transloco';
import { combineLatest, distinctUntilChanged, map, tap } from 'rxjs';
import { QuestionBankQuery } from '../../app/question-bank.query';
import { QuestionBankService } from '../../app/question-bank.service';
import {
  QuestionBankTopicsTreeComponent,
  QuestionBankTopicTreeNode,
} from '../question-bank-topics-tree/question-bank-topics-tree.component';

@Component({
  selector: 'dojo-question-bank-topics-explorer',
  templateUrl: './question-bank-topics-explorer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    QuestionBankTopicsTreeComponent,
    MatSlideToggleModule,
    ReactiveFormsModule,
    InfoIconComponent,
    TranslocoPipe,
  ],
})
export class QuestionBankTopicsExplorerComponent {
  constructor(
    private readonly categoryQuery: CategoryQuery,
    private readonly questionBankQuery: QuestionBankQuery,
    private readonly questionBankService: QuestionBankService,
  ) {
    this.questionBankQuery
      .selectProp('filters')
      .pipe(
        map((filters) => filters.topicMatchMode),
        distinctUntilChanged(),
        tap((topicMatchMode) => {
          this.includePartialMatchesCtrl.reset(topicMatchMode === 'ANY', { emitEvent: false });
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly state = connectState({
    explorerNodes: combineLatest([
      this.categoryQuery.activeSyllabusTopics$,
      this.questionBankQuery.selectProp('facets').pipe(distinctUntilDeepChanged()),
    ]).pipe(map(([topics, facets]) => this.mapTopicsToExplorerNodes(topics, facets))),
    selectedIds: this.questionBankQuery.selectProp('filters').pipe(
      map((filters) => (filters.topicIds ?? []).map((id) => id.toString())),
      distinctUntilDeepChanged(),
    ),
  });

  readonly includePartialMatchesCtrl = new FormControl(true);

  onMatchModeChange(checked: boolean) {
    this.questionBankService.changeFilters({
      topicMatchMode: checked ? 'ANY' : 'ALL',
    });
  }

  onSelectionChange(nodes: QuestionBankTopicTreeNode[]) {
    const topicIds = nodes.filter((node) => node.path.length === 2).map((node) => node.originalData!.id);

    this.questionBankService.changeFilters({ topicIds: topicIds });
  }

  private mapTopicsToExplorerNodes(
    topics: HydratedSyllabusTopicsTree,
    facets: Record<string, number>,
  ): QuestionBankTopicTreeNode[] {
    return topics.flatMap((l1) => {
      const l1Node: QuestionBankTopicTreeNode = {
        id: `tl1-${l1.id}`,
        name: l1.name,
        path: [`${l1.id}`],
        originalData: l1,
        metadata: {},
      };
      const l2Nodes = l1.children.map((l2) => ({
        id: l2.id.toString(),
        name: l2.name,
        path: [`${l1.id}`, `${l2.id}`],
        originalData: l2,
        metadata: { count: facets[l2.id] },
      }));
      return [l1Node, ...l2Nodes];
    });
  }
}
